import axios from "axios"
import { cacheLife } from "next/cache"
import { cache } from "react"
import type { WooCommerceProduct } from "@/services/woo-commerce-service"
import { parseTotalHeader } from "@/utilities/woo-commerce-helpers"
import { dokanApi, publicDokanApi, wooCommerceApi } from "./api"

interface DokanStoreRatingShape {
  rating?: string | number
  avg?: string | number
  average?: string | number
  count?: string | number
  total?: string | number
}

interface DokanStoreAddressShape {
  street_1?: string
  street_2?: string
  city?: string
  zip?: string
  country?: string
  state?: string
}

interface DokanStoreSocialShape {
  fb?: string
  twitter?: string
  pinterest?: string
  linkedin?: string
  youtube?: string
  instagram?: string
  flickr?: string
}

export interface DokanStorePayload {
  id: number
  slug?: string
  store_slug?: string
  store_name?: string
  first_name?: string
  last_name?: string
  email?: string
  show_email?: boolean
  phone?: string
  address?: DokanStoreAddressShape
  social?: DokanStoreSocialShape
  shop_name?: string
  shop_url?: string
  banner?: string | { url?: string }
  gravatar?: string | { url?: string }
  avatar?: string | { url?: string }
  rating?: DokanStoreRatingShape | string | number
}

export interface DokanStore {
  id: number
  name: string
  slug: string
  shopUrl: string | null
  bannerImageUrl: string | null
  avatarUrl: string | null
  ratingAverage: number | null
  ratingCount: number | null
  phone: string | null
  email: string | null
  addressLabel: string | null
  socials: Array<{ key: string; label: string; url: string }>
  payload: DokanStorePayload
}

type StoreProductsOrderBy =
  | "menu_order"
  | "date"
  | "id"
  | "title"
  | "popularity"
  | "rating"

type StoreProductsOrder = "asc" | "desc"

interface GetStoreProductsPageOptions {
  storeId: number
  page?: number
  per_page?: number
  q?: string
  orderby?: StoreProductsOrderBy
  order?: StoreProductsOrder
}

export interface StoreProductsPageResult {
  products: WooCommerceProduct[]
  totalPages: number
  totalProducts: number
}

interface DokanProductCategoryRef {
  id: number
  name: string
  slug: string
}

interface DokanStoreProductForCategories {
  id: number
  categories: DokanProductCategoryRef[]
}

interface WooProductCategoryNodePayload {
  id: number
  name: string
  slug: string
  parent: number
}

export interface StoreSidebarCategoryNode {
  id: number
  name: string
  slug: string
  count: number
  children: StoreSidebarCategoryNode[]
}

export interface DokanStoreReview {
  id: number
  author: {
    id: number | string
    name: string
    url: string
    avatar: string
  }
  title: string | null
  content: string
  permalink: string | null
  product_id: number | string | null
  approved: boolean
  date: string
  rating: number
}

export interface StoreReviewsPageResult {
  reviews: DokanStoreReview[]
  totalPages: number
  totalReviews: number
}

const isRecoverableStoreProductsError = (error: unknown): boolean =>
  axios.isAxiosError(error) &&
  (error.code === "ETIMEDOUT" ||
    error.code === "ERR_CANCELED" ||
    error.response?.status === 404)

const normalizeToken = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")

const readTextValue = (value: unknown): string | null =>
  typeof value === "string" && value.trim().length > 0 ? value.trim() : null

const readImageUrl = (value: unknown): string | null => {
  const directUrl = readTextValue(value)
  if (directUrl) {
    return directUrl
  }

  if (!value || typeof value !== "object") {
    return null
  }

  return readTextValue((value as { url?: unknown }).url)
}

const readNumberValue = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value
  }

  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number.parseFloat(value)
    return Number.isFinite(parsed) ? parsed : null
  }

  return null
}

const toHttpsUrl = (value: string): string => {
  if (value.startsWith("http://") || value.startsWith("https://")) {
    return value
  }

  return `https://${value}`
}

const buildAddressLabel = (
  address: DokanStoreAddressShape | undefined
): string | null => {
  if (!address) {
    return null
  }

  const parts = [
    readTextValue(address.street_1),
    readTextValue(address.street_2),
    readTextValue(address.city),
    readTextValue(address.state),
    readTextValue(address.zip),
    readTextValue(address.country),
  ].filter((part): part is string => Boolean(part))

  if (parts.length === 0) {
    return null
  }

  return parts.join(", ")
}

const buildSocials = (
  social: DokanStoreSocialShape | undefined
): Array<{ key: string; label: string; url: string }> => {
  if (!social) {
    return []
  }

  const socialConfig: Array<{
    key: keyof DokanStoreSocialShape
    label: string
  }> = [
    { key: "instagram", label: "Instagram" },
    { key: "fb", label: "Facebook" },
    { key: "twitter", label: "Twitter" },
    { key: "linkedin", label: "LinkedIn" },
    { key: "youtube", label: "YouTube" },
    { key: "pinterest", label: "Pinterest" },
    { key: "flickr", label: "Flickr" },
  ]

  const socials: Array<{ key: string; label: string; url: string }> = []

  for (const socialItem of socialConfig) {
    const rawUrl = readTextValue(social[socialItem.key])
    if (!rawUrl) {
      continue
    }

    socials.push({
      key: socialItem.key,
      label: socialItem.label,
      url: toHttpsUrl(rawUrl),
    })
  }

  return socials
}

const toShopUrlSlug = (shopUrl: string | null): string | null => {
  if (!shopUrl) {
    return null
  }

  try {
    const url = new URL(shopUrl)
    const pathTokens = url.pathname.split("/").filter(Boolean)
    const lastToken = pathTokens.at(-1)
    return lastToken ? normalizeToken(lastToken) : null
  } catch {
    const pathTokens = shopUrl.split("/").filter(Boolean)
    const lastToken = pathTokens.at(-1)
    return lastToken ? normalizeToken(lastToken) : null
  }
}

const getStoreFullName = (data: DokanStorePayload): string | null =>
  readTextValue(
    [readTextValue(data.first_name), readTextValue(data.last_name)]
      .filter((name): name is string => Boolean(name))
      .join(" ")
  )

const getStoreName = (data: DokanStorePayload): string =>
  readTextValue(data.store_name) ??
  readTextValue(data.shop_name) ??
  getStoreFullName(data) ??
  readTextValue(data.slug) ??
  readTextValue(data.store_slug) ??
  `Store ${data.id}`

const getStoreSlug = (
  data: DokanStorePayload,
  name: string,
  shopUrl: string | null
): string =>
  readTextValue(data.slug) ??
  readTextValue(data.store_slug) ??
  toShopUrlSlug(shopUrl) ??
  normalizeToken(name) ??
  `store-${data.id}`

const getStoreRatingShape = (
  rating: DokanStorePayload["rating"]
): DokanStoreRatingShape | null =>
  rating && typeof rating === "object" ? rating : null

const getStoreRatingAverage = (data: DokanStorePayload): number | null => {
  const ratingShape = getStoreRatingShape(data.rating)
  if (ratingShape) {
    return (
      readNumberValue(ratingShape.avg) ??
      readNumberValue(ratingShape.average) ??
      readNumberValue(ratingShape.rating)
    )
  }

  return readNumberValue(data.rating)
}

const getStoreRatingCount = (data: DokanStorePayload): number | null => {
  const ratingShape = getStoreRatingShape(data.rating)
  if (!ratingShape) {
    return null
  }

  return (
    readNumberValue(ratingShape.count) ?? readNumberValue(ratingShape.total)
  )
}

export const mapDokanStore = (data: DokanStorePayload): DokanStore => {
  const shopUrl = readTextValue(data.shop_url)
  const name = getStoreName(data)

  return {
    id: data.id,
    name,
    slug: getStoreSlug(data, name, shopUrl),
    shopUrl,
    bannerImageUrl: readImageUrl(data.banner),
    avatarUrl: readImageUrl(data.avatar) ?? readImageUrl(data.gravatar),
    ratingAverage: getStoreRatingAverage(data),
    ratingCount: getStoreRatingCount(data),
    phone: readTextValue(data.phone),
    email: data.show_email === false ? null : readTextValue(data.email),
    addressLabel: buildAddressLabel(data.address),
    socials: buildSocials(data.social),
    payload: data,
  }
}

export const getStoreById = cache(
  async (storeId: number): Promise<DokanStore | null> => {
    "use cache"

    cacheLife("hours")

    if (!Number.isInteger(storeId) || storeId <= 0) {
      return null
    }

    try {
      const { data } = await dokanApi.get<DokanStorePayload>(
        `/stores/${storeId}`
      )

      return mapDokanStore(data)
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null
      }

      throw error
    }
  }
)

export const getStoreBySlug = cache(
  async (storeSlug: string): Promise<DokanStore | null> => {
    "use cache"

    cacheLife("hours")

    const normalizedSlug = normalizeToken(storeSlug)
    if (!normalizedSlug) {
      return null
    }

    const { data } = await publicDokanApi.get<DokanStorePayload[]>("/stores", {
      params: {
        search: normalizedSlug,
        per_page: 100,
      },
    })

    const matchedStore = data
      .map((store) => mapDokanStore(store))
      .find((store) => store.slug === normalizedSlug)

    return matchedStore ?? null
  }
)

const getStoreProductsForCategories = async (
  storeId: number
): Promise<DokanStoreProductForCategories[]> => {
  const firstPageResponse = await publicDokanApi.get<
    DokanStoreProductForCategories[]
  >(`/stores/${storeId}/products`, {
    params: {
      page: 1,
      per_page: 100,
      status: "publish",
    },
  })

  const firstPageProducts = firstPageResponse.data
  const totalPages = parseTotalHeader(
    firstPageResponse.headers["x-wp-totalpages"]
  )

  if (totalPages <= 1) {
    return firstPageProducts
  }

  const allProducts = [...firstPageProducts]

  for (let page = 2; page <= totalPages; page += 1) {
    try {
      const pageResponse = await publicDokanApi.get<
        DokanStoreProductForCategories[]
      >(`/stores/${storeId}/products`, {
        params: {
          page,
          per_page: 100,
          status: "publish",
        },
      })

      allProducts.push(...pageResponse.data)
    } catch (error) {
      if (isRecoverableStoreProductsError(error)) {
        break
      }

      throw error
    }
  }

  return allProducts
}

const getWooCategoriesByIds = async (
  categoryIds: number[]
): Promise<WooProductCategoryNodePayload[]> => {
  if (categoryIds.length === 0) {
    return []
  }

  const pageSize = 100
  const pages = Math.ceil(categoryIds.length / pageSize)
  const requests: Promise<{ data: WooProductCategoryNodePayload[] }>[] = []

  for (let pageIndex = 0; pageIndex < pages; pageIndex += 1) {
    const startIndex = pageIndex * pageSize
    const pageIds = categoryIds.slice(startIndex, startIndex + pageSize)
    requests.push(
      wooCommerceApi.get<WooProductCategoryNodePayload[]>(
        "/products/categories",
        {
          params: {
            include: pageIds.join(","),
            per_page: pageSize,
          },
        }
      )
    )
  }

  const responses = await Promise.all(requests)
  const categories: WooProductCategoryNodePayload[] = []

  for (const response of responses) {
    categories.push(...response.data)
  }

  return categories
}

const sortCategoryTree = (tree: StoreSidebarCategoryNode[]): void => {
  tree.sort((a, b) => {
    if (b.count !== a.count) {
      return b.count - a.count
    }

    return a.name.localeCompare(b.name, "pt-BR")
  })

  for (const node of tree) {
    if (node.children.length > 0) {
      sortCategoryTree(node.children)
    }
  }
}

const buildCategoryTree = (
  categories: WooProductCategoryNodePayload[],
  countByCategoryId: Map<number, number>
): StoreSidebarCategoryNode[] => {
  const nodeByCategoryId = new Map<number, StoreSidebarCategoryNode>()

  for (const category of categories) {
    nodeByCategoryId.set(category.id, {
      id: category.id,
      name: category.name,
      slug: category.slug,
      count: countByCategoryId.get(category.id) ?? 0,
      children: [],
    })
  }

  const roots: StoreSidebarCategoryNode[] = []

  for (const category of categories) {
    const node = nodeByCategoryId.get(category.id)
    if (!node) {
      continue
    }

    const parentNode =
      category.parent > 0 ? nodeByCategoryId.get(category.parent) : null

    if (parentNode) {
      parentNode.children.push(node)
    } else {
      roots.push(node)
    }
  }

  sortCategoryTree(roots)
  return roots
}

interface StoreCategoryStats {
  countByCategoryId: Map<number, number>
  categoryNameById: Map<number, { name: string; slug: string }>
}

const collectStoreCategoryStats = (
  products: DokanStoreProductForCategories[]
): StoreCategoryStats => {
  const countByCategoryId = new Map<number, number>()
  const categoryNameById = new Map<number, { name: string; slug: string }>()

  for (const product of products) {
    for (const category of product.categories ?? []) {
      countByCategoryId.set(
        category.id,
        (countByCategoryId.get(category.id) ?? 0) + 1
      )

      if (!categoryNameById.has(category.id)) {
        categoryNameById.set(category.id, {
          name: category.name,
          slug: category.slug,
        })
      }
    }
  }

  return {
    countByCategoryId,
    categoryNameById,
  }
}

const buildFallbackCategoryTree = ({
  categoryIds,
  categoryNameById,
  countByCategoryId,
}: {
  categoryIds: number[]
  categoryNameById: Map<number, { name: string; slug: string }>
  countByCategoryId: Map<number, number>
}): StoreSidebarCategoryNode[] => {
  const nodes: StoreSidebarCategoryNode[] = categoryIds
    .map((categoryId): StoreSidebarCategoryNode | null => {
      const basicCategory = categoryNameById.get(categoryId)
      if (!basicCategory) {
        return null
      }

      return {
        id: categoryId,
        name: basicCategory.name,
        slug: basicCategory.slug,
        count: countByCategoryId.get(categoryId) ?? 0,
        children: [],
      }
    })
    .filter(
      (category): category is StoreSidebarCategoryNode => category !== null
    )

  nodes.sort((a, b) => b.count - a.count)

  return nodes
}

export const getStoreCategoriesTree = cache(
  async (storeId: number): Promise<StoreSidebarCategoryNode[]> => {
    "use cache"

    cacheLife("hours")

    try {
      const products = await getStoreProductsForCategories(storeId)
      const { countByCategoryId, categoryNameById } =
        collectStoreCategoryStats(products)
      const categoryIds = [...countByCategoryId.keys()]
      const detailedCategories = await getWooCategoriesByIds(categoryIds)

      if (detailedCategories.length === 0) {
        return buildFallbackCategoryTree({
          categoryIds,
          categoryNameById,
          countByCategoryId,
        })
      }

      return buildCategoryTree(detailedCategories, countByCategoryId)
    } catch (error) {
      if (isRecoverableStoreProductsError(error)) {
        return []
      }

      throw error
    }
  }
)

export const getStoreProductsPage = cache(
  async ({
    storeId,
    page = 1,
    per_page = 12,
    q,
    orderby = "menu_order",
    order = "asc",
  }: GetStoreProductsPageOptions): Promise<StoreProductsPageResult> => {
    "use cache"

    cacheLife("minutes")

    const { data: products, headers } = await publicDokanApi.get<
      WooCommerceProduct[]
    >(`/stores/${storeId}/products`, {
      params: {
        page,
        per_page,
        status: "publish",
        stock_status: "instock",
        orderby,
        order,
        ...(q ? { search: q } : {}),
      },
    })

    return {
      products,
      totalPages: parseTotalHeader(headers["x-wp-totalpages"]),
      totalProducts: parseTotalHeader(headers["x-wp-total"]),
    }
  }
)

export const getStoreReviewsPage = cache(
  async ({
    storeId,
    page = 1,
    perPage = 25,
  }: {
    storeId: number
    page?: number
    perPage?: number
  }): Promise<StoreReviewsPageResult> => {
    "use cache"

    cacheLife("minutes")

    let response: Awaited<
      ReturnType<typeof publicDokanApi.get<DokanStoreReview[]>>
    >

    try {
      response = await publicDokanApi.get<DokanStoreReview[]>(
        `/stores/${storeId}/reviews`,
        {
          params: {
            page,
            per_page: perPage,
          },
        }
      )
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return {
          reviews: [],
          totalPages: 1,
          totalReviews: 0,
        }
      }

      throw error
    }

    return {
      reviews: response.data,
      totalPages: parseTotalHeader(response.headers["x-wp-totalpages"]),
      totalReviews: parseTotalHeader(response.headers["x-wp-total"]),
    }
  }
)
